import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormViewer from "../components/UI/FormViewer";
import Loader from "../components/UI/Loader";
import { getClientFormInstance } from "../services/formsService";
import { deleteFileUpload, getClientRelatedFiles, getDownloadUrl } from "../services/fileService";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { ClientRelatedFile } from "../models/serviceModels/ClientRelatedFile";
import { format } from "date-fns";
import IconButton from "../components/UI/IconButton";
import Dropdown from "../components/UI/Dropdown";
import Modal from "../components/UI/Modal";
import Button from "../components/UI/Button";
import ParticipantPublicSubmissions from "./ParticipantPublicSubmissions";
import { publicSubmissionFollowUpsEnabled, sortableParticipantFilesEnabled } from "../utils/features";
import DataTable from "../components/UI/DataTable";
import useSortableData from "../hooks/useSortableData";
import { DataTableColumn } from "../models/DataTable";

const ParticipantAdditionalInformation = () => {
    const { id = '' } = useParams();
    const [loading, setLoading] = useState(true);
    const [clientForm, setClientForm] = useState<any>(null);
    const [apiError, setApiError] = useState<any>();
    const [relatedFiles, setRelatedFiles] = useState<ClientRelatedFile[]>([]);
    const [filesExpanded, setFilesExpanded] = useState(false);
    const [openingFiles, setOpeningFiles] = useState<string[]>([]);
    const [fileToRemove, setFileToRemove] = useState<ClientRelatedFile>();
    const [deletingFile, setDeletingFile] = useState(false);
    const [modalApiError, setModalApiError] = useState<any>();
    const getSortValue = useCallback((file: ClientRelatedFile, key: string) => {
        if (key === 'source') return getFileSource(file);
        return file[key as keyof ClientRelatedFile];
    }, []);
    const { data: sortedRelatedFiles, sortBy, sortDirection, onSort } = useSortableData(relatedFiles, getSortValue);

    useEffect(() => {
        Promise.all([getClientFormInstance(id), getClientRelatedFiles(id)]).then(([fi, rf]) => {
            setRelatedFiles(rf);
            setClientForm(fi);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const getFileSource = (file: ClientRelatedFile) => {
        if (file.publicSubmission) {
            return 'Public Submission';
        }

        switch(file.referenceType) {
            case 'case_plan': return `Case Plan (${file.referenceDescription})`;
            case 'client': return 'Profile';
            case 'client_note': return 'Note';
            case 'form_instance_field_file': return `Form Submission (${file.referenceDescription})`;
            default: return ''
        }
    };

    const handleOnOpen = (fileId: string) => {
        setOpeningFiles([
            ...openingFiles,
            fileId
        ]);
        
        getDownloadUrl({
            fileId
        }).then(r => {
            if (r.url) {
                const anchor = document.createElement('a');
                anchor.href = r.url;
                anchor.setAttribute('download', 'download');
                anchor.click();
            }
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setOpeningFiles([
                ...openingFiles.filter(o => o !== fileId)
            ]);
        });
    };

    const handleOnConfirmDeleteFile = (file: ClientRelatedFile) => {
        setFileToRemove(file);
        setDeletingFile(false);
        setModalApiError(undefined);
    };

    const handleSort = (key: string) => {
        onSort(key);
        if (!sortableParticipantFilesEnabled) return;

        const nextDirection = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc';
        getClientRelatedFiles(id, key, nextDirection)
            .then(setRelatedFiles)
            .catch(e => {
                console.error(e);
                setApiError(e);
            });
    };

    const handleOnCancelConfirmDelete = () => {
        setFileToRemove(undefined);
        setDeletingFile(false);
        setModalApiError(undefined);
    };

    const handleOnDeleteFile = () => {
        if (!fileToRemove) {
            console.warn('No file selected, unable to delete');
            return;
        }

        setDeletingFile(true);

        deleteFileUpload({
            fileId: fileToRemove.fileId
        }).then(() => {
            getClientRelatedFiles(id).then(rf => {
                setRelatedFiles(rf);
                handleOnCancelConfirmDelete();
            }).catch(e => {
                console.error(e);
                setModalApiError(e);
            }).finally(() => {
                setDeletingFile(false);
            })
        }).catch(e => {
            console.error(e);
            setModalApiError(e);
            setDeletingFile(false);
        });
    }

    if (loading) {
        return <Loader />
    }

    return (
        <>
            <ApiErrorMessages error={apiError} />
            {clientForm && (<div>
                <div className="row">
                    <div className="column-auto">
                        <h2>Additional Information</h2>
                    </div>
                </div>
                <FormViewer instance={clientForm} />
            </div>)}
            {publicSubmissionFollowUpsEnabled && <ParticipantPublicSubmissions clientId={id} collapsible={true} />}
            <div className="row padding-2 margin-top-3 border-bottom">
                <div className="column">
                    <div className="row align-items-center">
                        <div className="column-auto">
                            <IconButton className="small flat"
                                id="expand-collapse-profile"
                                icon={filesExpanded ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                                ariaLabel={`${filesExpanded ? 'Hide' : 'Show'} All Participant Files (${relatedFiles.length})`}
                                ariaExpanded={filesExpanded}
                                ariaControls="all-participant-files-content"
                                onClick={() => setFilesExpanded(!filesExpanded)} />
                        </div>
                        <div className="column-auto">
                            <h2>All Participant Files ({relatedFiles.length})</h2>
                        </div>
                        <div className="column-auto">
                            {openingFiles.length !== 0 && <Loader />}
                        </div>
                    </div>
                    {filesExpanded && <div id="all-participant-files-content" className="row margin-bottom-3">
                        <div className="column">
                            <DataTable
                                columns={[
                                    { key: 'fileName', label: 'file name', sortable: true },
                                    { key: 'source', label: 'source', sortable: true },
                                    { key: 'fileCreated', label: 'uploaded', sortable: true },
                                    { key: 'actions', label: '', sortable: false }
                                ] as DataTableColumn[]}
                                data={sortedRelatedFiles}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                sortingEnabled={sortableParticipantFilesEnabled}
                                emptyMessage="No files found"
                                renderRow={rf => {
                                        const options: {label: string; href?: string; onClick?: () => void; newWindow?: boolean;}[] = [
                                            {
                                                label: 'Open File',
                                                onClick: () => handleOnOpen(rf.fileId)
                                            }
                                        ];

                                        if (rf.referenceType === 'case_plan') {
                                            options.push({
                                                label: 'Open Case Plan',
                                                href: `/participants/${id}/plans/${rf.primaryReferenceId}`,
                                                newWindow: true
                                            });
                                        } else if (rf.referenceType === 'client_note') {
                                            options.push({
                                                label: 'Open Note',
                                                href: `/participants/${id}/notes/${rf.primaryReferenceId}`,
                                                newWindow: true
                                            })
                                        } else if (rf.referenceType === 'form_instance_field_file') {
                                            options.push({
                                                label: 'Open Form Submission',
                                                href: `/forms/${rf.primaryReferenceId}/versions/${rf.secondaryReferenceId}/instances/${rf.teriaryReferenceId}/view`,
                                                newWindow: true
                                            })
                                        }

                                        options.push({
                                            label: 'Delete File',
                                            onClick: () => handleOnConfirmDeleteFile(rf)
                                        });

                                        return <tr key={rf.fileId}>
                                            <td>{rf.fileName}{rf.fileExtension}</td>
                                            <td>{getFileSource(rf)}</td>
                                            <td>{format(new Date(rf.fileCreated), 'Pp')}</td>
                                            <td>
                                                <Dropdown disabled={openingFiles.length !== 0} 
                                                    id={`participant-files-options-${rf.fileId}`} 
                                                    options={options} 
                                                    optionSize="large" />
                                            </td>
                                        </tr>
                                    }} />
                        </div>
                    </div>}
                </div>
            </div>
            <Modal id="file-upload-delete-modal" visible={fileToRemove !== undefined} className="small" title="Confirm File Remove" onClose={handleOnCancelConfirmDelete}>
                <>
                    <ApiErrorMessages error={modalApiError} />
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to remove this file?</p>
                            <p>{fileToRemove?.fileName}</p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-remove" 
                                        text="No" 
                                        className="secondary" 
                                        disabled={deletingFile}
                                        onClick={handleOnCancelConfirmDelete} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-remove" 
                                        text="Yes" 
                                        className="warning" 
                                        disabled={deletingFile}
                                        loading={deletingFile}
                                        onClick={handleOnDeleteFile} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
        </>
    );
};

export default ParticipantAdditionalInformation;
